// ============================================================================
// PublicFooter — pied de page global
// ============================================================================
// Accessible sur toutes les pages (publiques et authentifiées) depuis le
// ProtectedLayout et les layouts publics. Regroupe :
//   - Un lien interne vers le catalogue de formations (/plateforme).
//   - Un lien interne vers la page "Nous contacter" (/contact).
//   - Les documents généraux de l'organisme (Règlement intérieur, CGV, CGU,
//     Mentions légales, Politique RGPD) sous forme de liens externes vers
//     cecodev.fr, ouverts dans un nouvel onglet car le domaine est distinct.
// ============================================================================

import { Link as RouterLink } from "react-router-dom";
import { Box, Container, Typography, Stack, Divider, Link } from "@mui/material";
import { ExternalLink } from "lucide-react";
import { EXTERNAL_LINKS } from "../../lib/external-links";

interface FooterLink {
  label: string;
  to?: string;      // lien interne (react-router)
  href?: string;    // lien externe
  external?: boolean;
}

const LIENS_APP: FooterLink[] = [
  { label: "Catalogue de formations", to: "/plateforme" },
  { label: "Nous contacter", to: "/contact" },
];

const LIENS_LEGAUX: FooterLink[] = [
  { label: "Règlement intérieur", href: EXTERNAL_LINKS.reglementInterieur, external: true },
  // CGV : servies par l'app (/cgv = version active de la base, celle que la
  // case du tunnel fait accepter) — plus le site vitrine (audit 2026-09-18).
  { label: "Conditions générales de vente", to: "/cgv" },
  // Conditions d'usage par les organisations clientes (lot 8.5) : même
  // principe, la version active de la base, celle que le pilote accepte.
  { label: "Conditions d'usage", to: "/conditions-usage" },
  { label: "Conditions générales d'utilisation", href: EXTERNAL_LINKS.cgu, external: true },
  { label: "Mentions légales", href: EXTERNAL_LINKS.mentionsLegales, external: true },
];

function FooterLinkItem({ link }: { link: FooterLink }) {
  const sx = {
    color: "text.secondary",
    fontSize: "0.8125rem",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    "&:hover": { color: "primary.main", textDecoration: "underline" },
  } as const;

  if (link.external && link.href) {
    return (
      <Link href={link.href} target="_blank" rel="noopener noreferrer" sx={sx}>
        {link.label}
        <ExternalLink size={12} />
      </Link>
    );
  }
  if (link.to) {
    return (
      <Link component={RouterLink} to={link.to} sx={sx}>
        {link.label}
      </Link>
    );
  }
  return null;
}

export function PublicFooter() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        py: 3,
        px: 2,
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />}
        >
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {LIENS_APP.map((link) => (
              <FooterLinkItem key={link.label} link={link} />
            ))}
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {LIENS_LEGAUX.map((link) => (
              <FooterLinkItem key={link.label} link={link} />
            ))}
          </Stack>
        </Stack>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mt: 2, textAlign: { xs: "left", md: "right" } }}
        >
          © {year} CECODEV — Organisme de formation
        </Typography>
      </Container>
    </Box>
  );
}
